import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Filter, X } from "lucide-react";

const SIZE_OPTIONS = [
  { value: "S", label: "Lille (< 4.2 m)", description: "Smart, Fiat 500" },
  { value: "M", label: "Medium (4.2–4.8 m)", description: "VW Golf, Toyota Corolla" },
  { value: "L", label: "Stor (4.8–5.2 m)", description: "BMW 5-serie, Volvo XC90" },
  { value: "XL", label: "Ekstra stor (> 5.2 m)", description: "Varevogn, stor SUV" },
];

const FACILITY_OPTIONS = [
  { key: "charger", label: "El-lader", description: "Opladning til elbiler" },
  { key: "covered", label: "Overdækket", description: "Beskyttelse mod vejr" },
  { key: "camera", label: "Kamera", description: "Ekstra sikkerhed" },
];

interface SearchFiltersProps {
  isMobile?: boolean;
}

export default function SearchFilters({ isMobile = false }: SearchFiltersProps) {
  const [sp, setSp] = useSearchParams();
  
  const [priceRange, setPriceRange] = useState([
    parseInt(sp.get("minPrice") || "0"),
    parseInt(sp.get("maxPrice") || "500")
  ]);
  
  const selectedSizes = sp.get("sizes")?.split(",").filter(Boolean) || [];
  const hasCharger = sp.get("charger") === "true";
  const hasCovered = sp.get("covered") === "true";
  const hasCamera = sp.get("camera") === "true";

  const hasActiveFilters = 
    priceRange[0] > 0 || 
    priceRange[1] < 500 || 
    selectedSizes.length > 0 || 
    hasCharger || 
    hasCovered || 
    hasCamera;

  const toggleSize = (size: string) => {
    const newSizes = selectedSizes.includes(size)
      ? selectedSizes.filter(s => s !== size)
      : [...selectedSizes, size];
    
    const params = new URLSearchParams(sp);
    if (newSizes.length > 0) {
      params.set("sizes", newSizes.join(","));
    } else {
      params.delete("sizes");
    }
    setSp(params);
  };

  const toggleFacility = (key: string) => {
    const params = new URLSearchParams(sp);
    const current = params.get(key) === "true";
    
    if (current) {
      params.delete(key);
    } else {
      params.set(key, "true");
    }
    setSp(params);
  };

  const applyPriceFilter = () => {
    const params = new URLSearchParams(sp);
    
    if (priceRange[0] > 0) {
      params.set("minPrice", priceRange[0].toString());
    } else {
      params.delete("minPrice");
    }
    
    if (priceRange[1] < 500) {
      params.set("maxPrice", priceRange[1].toString());
    } else {
      params.delete("maxPrice");
    }
    
    setSp(params);
  };

  const clearAllFilters = () => {
    const params = new URLSearchParams();
    const query = sp.get("query");
    const from = sp.get("from");
    const to = sp.get("to");
    
    if (query) params.set("query", query);
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    
    setSp(params);
    setPriceRange([0, 500]);
  };

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Header with clear button */}
      {hasActiveFilters && (
        <div className="flex items-center justify-between">
          <Badge variant="secondary">
            {[
              priceRange[0] > 0 || priceRange[1] < 500 ? 1 : 0,
              selectedSizes.length,
              hasCharger ? 1 : 0,
              hasCovered ? 1 : 0,
              hasCamera ? 1 : 0,
            ].reduce((a, b) => a + b, 0)} aktive filtre
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="h-8 text-xs"
          >
            <X className="h-3 w-3 mr-1" />
            Nulstil alle
          </Button>
        </div>
      )}

      {/* Price Range */}
      <div className="space-y-4">
        <div>
          <Label className="text-base font-semibold">Pris pr. dag</Label>
          <p className="text-sm text-muted-foreground mt-1">
            {priceRange[0]} kr - {priceRange[1] === 500 ? "500+ kr" : `${priceRange[1]} kr`}
          </p>
        </div>
        
        <Slider
          min={0}
          max={500}
          step={10}
          value={priceRange}
          onValueChange={setPriceRange}
          onValueCommit={applyPriceFilter}
          className="py-4"
        />
      </div>

      <Separator />

      {/* Size Selection */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Størrelse</Label>
        <div className="space-y-2">
          {SIZE_OPTIONS.map((size) => (
            <div key={size.value} className="flex items-start space-x-2">
              <Checkbox
                id={`size-${size.value}`}
                checked={selectedSizes.includes(size.value)}
                onCheckedChange={() => toggleSize(size.value)}
              />
              <div className="grid gap-0.5 leading-none">
                <Label
                  htmlFor={`size-${size.value}`}
                  className="text-sm font-medium cursor-pointer"
                >
                  {size.label}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {size.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Separator />

      {/* Facilities */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Faciliteter</Label>
        <div className="space-y-2">
          {FACILITY_OPTIONS.map((facility) => (
            <div key={facility.key} className="flex items-start space-x-2">
              <Checkbox
                id={`facility-${facility.key}`}
                checked={
                  facility.key === "charger" ? hasCharger :
                  facility.key === "covered" ? hasCovered :
                  hasCamera
                }
                onCheckedChange={() => toggleFacility(facility.key)}
              />
              <div className="grid gap-0.5 leading-none">
                <Label
                  htmlFor={`facility-${facility.key}`}
                  className="text-sm font-medium cursor-pointer"
                >
                  {facility.label}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {facility.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Mobile version with Sheet
  if (isMobile) {
    return (
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="outline" className="w-full">
            <Filter className="h-4 w-4 mr-2" />
            Filtre
            {hasActiveFilters && (
              <Badge variant="secondary" className="ml-2">
                {[
                  priceRange[0] > 0 || priceRange[1] < 500 ? 1 : 0,
                  selectedSizes.length,
                  hasCharger ? 1 : 0,
                  hasCovered ? 1 : 0,
                  hasCamera ? 1 : 0,
                ].reduce((a, b) => a + b, 0)}
              </Badge>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[300px] sm:w-[400px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Filtre</SheetTitle>
            <SheetDescription>
              Filtrer søgeresultaterne efter dine præferencer
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            <FilterContent />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop version with Card
  return (
    <Card className="sticky top-20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Filter className="h-5 w-5" />
          Filtre
        </CardTitle>
      </CardHeader>
      <CardContent>
        <FilterContent />
      </CardContent>
    </Card>
  );
}
